import React from 'react';
import { Task } from '@spike/task-model';
import styles from './TaskInfo.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBowlFood, faSuitcaseMedical } from '@fortawesome/pro-regular-svg-icons';
import { useMasterData } from 'hooks';
import { useMediaQuery } from 'react-responsive';
import { careOf } from '../../careTaskTypes';

interface TaskInfoProps {
    task: Task;
}

export const TaskInfo: React.FC<TaskInfoProps> = ({ task }) => {
    const masterdata = useMasterData();
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const care = careOf(task);

    const getTaskType = () => {
        if (task.feeding) return 'feeding';
        if (task.medication) return 'medication';
        if (care) return 'care';
        return 'other';
    };

    const getTaskIcon = () => {
        const type = getTaskType();
        switch (type) {
            case 'feeding':
                return (
                    <div className={`${styles.icon} ${styles.iconFeeding}`}>
                        <FontAwesomeIcon icon={faBowlFood} />
                    </div>
                );
            case 'medication':
                return (
                    <div className={`${styles.icon} ${styles.iconMedication}`}>
                        <FontAwesomeIcon icon={faSuitcaseMedical} />
                    </div>
                );
            case 'care': {
                if (!care?.icon) return null;
                const colorClass = {
                    hydration: styles.iconHydration,
                    relief: styles.iconRelief,
                    addon: styles.iconAddon
                }[care.subtype];
                return (
                    <div className={`${styles.icon} ${colorClass}`}>
                        <FontAwesomeIcon icon={care.icon} />
                    </div>
                );
            }
            default:
                return null;
        }
    };

    const getTaskTitle = () => {
        if (task.feeding) {
            const foodType = masterdata.foodTypes.find(ft => ft.id === task.feeding?.foodTypeId)?.name;
            const quantity = task.feeding?.foodQuantity;
            const unit = masterdata.foodQuantityUnits.find(fq => fq.id === task.feeding?.foodQuantityUnitId)?.name;
            return `${foodType} • ${quantity} ${unit}`;
        }
        if (task.medication) {
            return task.medication.medicationName ?? 'Medication';
        }
        if (care) {
            return care.name;
        }
        return 'Task';
    };

    const getTaskSubtitle = () => {
        if (task.feeding) {
            const brand = task.feeding.feedingBrand ?? '';
            const provisionerType = masterdata.foodProvisionTypes.find(
                fpt => fpt.id === task.feeding?.foodProvisionerTypeId
            )?.name;

            if (isMobile) {
                return (
                    <>
                        {brand && <div className={styles.brand}>{brand}</div>}
                        {provisionerType && <div className={styles.provisioner}>{provisionerType}</div>}
                    </>
                );
            }

            return [brand, provisionerType].filter(Boolean).join(', ');
        }

        if (task.medication) {
            const method = masterdata.medicationMethodTypes.find(
                mmt => mmt.id === task.medication?.medicationMethodId
            )?.name;
            const dosageUnit = masterdata.medicationDosageUnits.find(
                mdu => mdu.id === task.medication?.dosageUnitId
            )?.name;
            const dosage = task.medication?.dosageAmount ? `${task.medication.dosageAmount} ${dosageUnit}` : undefined;
            return [method, dosage].filter(Boolean).join(' ');
        }
        return null;
    };

    return (
        <div className={styles.listItem}>
            <div className={styles.iconContainer}>{getTaskIcon()}</div>
            <div className={styles.texts}>
                <div className={styles.title}>{getTaskTitle()}</div>
                {getTaskSubtitle() && <div className={styles.subtitle}>{getTaskSubtitle()}</div>}
            </div>
        </div>
    );
};

export default TaskInfo;
